import React, { useCallback, useEffect, useRef, useState } from 'react';
import Reveal from './Reveal';
import { CheckIcon, DailyLogsIcon, DrawingsIcon, PunchIcon, RfiIcon } from './icons';
import {
  DailyBody,
  DrawingBody,
  PunchListBody,
  RfiBody,
  ScheduleBody,
  scenes,
} from './phoneScenes';
import './styles.scss';

const sceneCount = scenes.length;

const checkPills = ['Offline-ready', 'Photo-stamped', 'Owner-ready PDF export'];

export default function FieldSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // The index the carousel is headed to — updated synchronously so rapid
  // clicks during the 400ms crossfade compute from the right base.
  const pendingIndex = useRef(0);
  const fadeTimer = useRef(0);

  // Finger tap animation state
  const [fingerStep, setFingerStep] = useState(0);
  const [isTapping, setIsTapping] = useState(false);
  const [fingerVisible, setFingerVisible] = useState(false);
  const [revealedCount, setRevealedCount] = useState(0);

  const reducedMotion = useRef(false);
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      reducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }, []);

  const select = useCallback((idx) => {
    pendingIndex.current = idx;
    window.clearTimeout(fadeTimer.current);
    setIsFading(true);
    fadeTimer.current = window.setTimeout(() => {
      setActiveIndex(idx);
      setIsFading(false);
    }, 400);
  }, []);

  useEffect(() => () => window.clearTimeout(fadeTimer.current), []);

  const handleSelect = (idx) => {
    if (idx === pendingIndex.current) return;
    setIsPaused(true);
    select(idx);
  };

  const step = (dir) => {
    setIsPaused(true);
    select((pendingIndex.current + dir + sceneCount) % sceneCount);
  };

  // Auto-advance every 6s until the user interacts
  useEffect(() => {
    if (isPaused || reducedMotion.current) return undefined;
    const id = setInterval(() => {
      select((pendingIndex.current + 1) % sceneCount);
    }, 6000);
    return () => clearInterval(id);
  }, [isPaused, select]);

  const scene = scenes[activeIndex];

  // Sequence finger taps when the scene changes
  useEffect(() => {
    setFingerStep(0);
    setIsTapping(false);
    setFingerVisible(false);
    setRevealedCount(0);
    if (reducedMotion.current) return undefined;

    const targets = scene.fingerTargets || [];
    if (targets.length === 0) return undefined;

    let cancelled = false;
    const timeouts = [];
    let idx = 0;

    timeouts.push(
      setTimeout(() => {
        if (!cancelled) setFingerVisible(true);
      }, 600),
    );

    const tick = () => {
      if (cancelled) return;
      setFingerStep(idx);
      setIsTapping(false);
      timeouts.push(
        setTimeout(() => {
          if (cancelled) return;
          setIsTapping(true);
          timeouts.push(
            setTimeout(() => {
              if (cancelled) return;
              setIsTapping(false);
              const newRevealed = idx + 1;
              const next = newRevealed % targets.length;
              if (next === 0) {
                setRevealedCount(targets.length);
                timeouts.push(
                  setTimeout(() => {
                    if (cancelled) return;
                    setRevealedCount(0);
                    idx = 0;
                    tick();
                  }, 1100),
                );
              } else {
                setRevealedCount(newRevealed);
                idx = next;
                tick();
              }
            }, 380),
          );
        }, 760),
      );
    };

    timeouts.push(setTimeout(tick, 950));

    return () => {
      cancelled = true;
      timeouts.forEach(clearTimeout);
    };
  }, [activeIndex, scene]);

  const renderBody = () => {
    switch (scene.key) {
      case 'rfi':
        return <RfiBody body={scene.body} />;
      case 'drawing':
        return <DrawingBody body={scene.body} />;
      case 'punch':
        return (
          <PunchListBody
            body={scene.body}
            revealedCount={revealedCount}
            currentStep={fingerStep}
            isTapping={isTapping}
          />
        );
      case 'daily':
        return <DailyBody body={scene.body} currentStep={fingerStep} isTapping={isTapping} />;
      case 'schedule':
        return <ScheduleBody body={scene.body} currentStep={fingerStep} isTapping={isTapping} />;
      default:
        return null;
    }
  };

  const target = scene.fingerTargets?.[fingerStep];

  return (
    <section className="plh-field">
      <div className="plh-field__inner">
        <Reveal className="plh-field__copy">
          <p className="plh-eyebrow">In the field</p>
          <h2>
            Eliminate end-of-day paperwork.{' '}
            <span className="plh-accent">Capture the ground truth.</span>
          </h2>
          <p>
            Two minutes a day captures everything — weather auto-pulled, crews, hours, equipment,
            photos, and progress notes, with voice-to-text for the rest.
          </p>
          <p>
            Drawings are cached on device, so your foreman doesn&rsquo;t lose 30 minutes to a
            missing plan. Offline at the slab edge; synced to the record the moment you&rsquo;re
            back. iOS and Android, on every plan, with two-minute onboarding.
          </p>
          <div className="plh-field__pills">
            {checkPills.map((pill) => (
              <span className="plh-check-pill" key={pill}>
                <CheckIcon />
                {pill}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal
          className="plh-field__visual"
          delay={100}
          onMouseEnter={() => setIsPaused(true)}
        >
          <div className="plh-field__device">
            <div className="pl-mobile-showcase__phone plh-field__phone" data-feature={scene.key}>
              <div className="pl-mobile-showcase__phone-notch" />
              <div className="pl-mobile-showcase__phone-screen">
                <div className="pl-mobile-showcase__app-bar">
                  <span className="pl-mobile-showcase__app-back">‹ Back</span>
                  <span className="pl-mobile-showcase__app-title">{scene.appTitle}</span>
                  <span className="pl-mobile-showcase__app-menu">⋯</span>
                </div>
                <div className={`pl-mobile-showcase__app-body${isFading ? ' is-fading' : ''}`}>
                  <div className="pl-mobile-showcase__screen-head">
                    <span className="pl-mobile-showcase__screen-title">{scene.screenTitle}</span>
                  </div>
                  <div className="pl-mobile-showcase__screen-status">
                    <span className="pl-mobile-showcase__screen-label">{scene.statusLabel}</span>
                    <span
                      className={`pl-mobile-showcase__screen-badge pl-mobile-showcase__screen-badge--${scene.statusTone}`}
                    >
                      {scene.statusBadge}
                    </span>
                  </div>
                  <div
                    className={`pl-mobile-showcase__screen-body pl-mobile-showcase__screen-body--${scene.key}`}
                  >
                    {renderBody()}
                  </div>
                </div>
                <div className="pl-mobile-showcase__app-tabbar">
                  <span className="pl-mobile-showcase__app-tab is-active">
                    <RfiIcon />
                  </span>
                  <span className="pl-mobile-showcase__app-tab">
                    <DrawingsIcon />
                  </span>
                  <span className="pl-mobile-showcase__app-tab pl-mobile-showcase__app-tab--cta">
                    +
                  </span>
                  <span className="pl-mobile-showcase__app-tab">
                    <PunchIcon />
                  </span>
                  <span className="pl-mobile-showcase__app-tab">
                    <DailyLogsIcon />
                  </span>
                </div>
                {target && (
                  <div
                    className={`pl-mobile-showcase__finger${fingerVisible ? ' is-visible' : ''}${isTapping ? ' is-tapping' : ''}${isFading ? ' is-hidden' : ''}`}
                    style={{ top: target.top, left: target.left }}
                    aria-hidden="true"
                  >
                    <span className="pl-mobile-showcase__tap-glow" />
                    <span className="pl-mobile-showcase__tap-ring" />
                    <span className="pl-mobile-showcase__tap-ring pl-mobile-showcase__tap-ring--echo" />
                    <span className="pl-mobile-showcase__tap-dot" />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="plh-field__carousel" role="group" aria-label="Field workflows">
            <button
              type="button"
              className="plh-field__arrow"
              aria-label="Previous workflow"
              onClick={() => step(-1)}
            >
              ‹
            </button>
            <div className="plh-field__chips">
              {scenes.map((s, idx) => (
                <button
                  type="button"
                  aria-pressed={idx === activeIndex}
                  key={s.key}
                  className={`plh-field__chip${idx === activeIndex ? ' is-active' : ''}`}
                  onClick={() => handleSelect(idx)}
                >
                  {s.pill}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="plh-field__arrow"
              aria-label="Next workflow"
              onClick={() => step(1)}
            >
              ›
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
